import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PhygitalIcon from "@/components/PhygitalIcon";

const PrivacyPage = () => {
  const lastUpdated = "March 30, 2026";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-gray-700 hover:text-gray-900">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Link>
            <div className="flex items-center gap-2">
              <PhygitalIcon className="w-6 h-6" />
              <span className="font-semibold">phygital</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-600 mb-8">Last updated: {lastUpdated}</p>

        <div className="prose prose-gray max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4">
              Phygital ("we", "our", "us") is committed to protecting your privacy. This Privacy Policy explains 
              how we collect, use, disclose, and safeguard your information when you use our preference-sharing platform.
            </p>
          </section>

          {/* Important: Data Not Sold Promise */}
          <section className="mb-8 p-6 bg-green-50 border border-green-200 rounded-xl">
            <h2 className="text-xl font-semibold text-green-800 mb-4 flex items-center gap-2">
              🔒 Our Data Promise
            </h2>
            <div className="space-y-3 text-green-900">
              <p className="font-medium">
                <strong>We never sell your personal data.</strong> Period.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Your data belongs to you.</strong> We are custodians, not owners.</li>
                <li><strong>You control what you share.</strong> Preferences are only visible to service providers and event hosts you explicitly choose to share with.</li>
                <li><strong>No third-party advertising.</strong> We don't sell your information to advertisers or data brokers.</li>
                <li><strong>Export or delete anytime.</strong> Download your complete profile or permanently delete your account at any time.</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-lg font-medium text-gray-800 mb-3">Personal Information</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Account Data:</strong> Phone number, email, name (via Google OAuth)</li>
              <li><strong>Profile Data:</strong> Profile photo, display name</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-3">Preference Data</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Event Preferences:</strong> Accessibility needs, dietary restrictions, interests, conversation preferences, music tastes</li>
              <li><strong>Beauty Preferences:</strong> Hair type, style preferences, product allergies, inspiration images</li>
              <li><strong>Inspiration Data:</strong> Photos, links, and references you save to category folders</li>
              <li><strong>Experience Plans:</strong> Planned services, linked events, provider selections, and notes</li>
              <li><strong>Custom Preferences:</strong> Any additional preferences you choose to share</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-3">Usage Data</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Events you RSVP to</li>
              <li>Providers you share preferences with</li>
              <li>Device information and IP address</li>
              <li>Pages visited and features used</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Service Delivery:</strong> Share your preferences with hosts/providers you authorize</li>
              <li><strong>Digital Passes:</strong> Generate QR codes and digital wallet passes for events</li>
              <li><strong>Personalization:</strong> Remember your preferences across events and appointments</li>
              <li><strong>Analytics:</strong> Aggregate preference data to help hosts plan better events (anonymized)</li>
              <li><strong>Communication:</strong> Send event updates, appointment reminders, and service notifications</li>
              <li><strong>Improvement:</strong> Enhance our platform based on usage patterns</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Information Sharing</h2>
            <p className="text-gray-700 mb-4">We share your information only in these circumstances:</p>
            
            <h3 className="text-lg font-medium text-gray-800 mb-3">With Your Consent</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Event Hosts:</strong> When you RSVP, hosts see your event preferences</li>
              <li><strong>Service Providers:</strong> When you share your profile link, providers see your beauty preferences</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-3">For Service Operation</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Infrastructure Providers:</strong> Cloud hosting, database services (data is encrypted)</li>
              <li><strong>Analytics:</strong> Aggregated, anonymized data only</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-3">Legal Requirements</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>When required by law or legal process</li>
              <li>To protect our rights or safety of users</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Sensitive Information</h2>
            <p className="text-gray-700 mb-4">
              Some preferences may be considered sensitive (accessibility needs, dietary restrictions related to health or religion). We handle this data with extra care:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Only shared with hosts/providers you explicitly authorize</li>
              <li>Used solely for accommodation purposes</li>
              <li>Never sold or used for advertising</li>
              <li>Can be deleted at any time upon request</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Data Security</h2>
            <p className="text-gray-700 mb-4">We protect your data through:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Encryption in transit (HTTPS) and at rest</li>
              <li>Secure authentication via phone verification or Google OAuth</li>
              <li>Access controls limiting who can view your preferences</li>
              <li>Regular security audits</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Your Rights</h2>
            <p className="text-gray-700 mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Access:</strong> View all data we have about you</li>
              <li><strong>Correct:</strong> Update your preferences at any time</li>
              <li><strong>Delete:</strong> Remove your account and all associated data</li>
              <li><strong>Export:</strong> Download your preference data</li>
              <li><strong>Restrict:</strong> Limit how specific preferences are shared</li>
              <li><strong>Withdraw Consent:</strong> Stop sharing with specific hosts/providers</li>
            </ul>
            <p className="text-gray-700 mb-4">
              To exercise these rights, contact us at privacy@phygital.com or use the settings in your profile.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Data Retention</h2>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Active Accounts:</strong> Data retained while your account is active</li>
              <li><strong>Event Data:</strong> RSVP preferences retained for 1 year after event</li>
              <li><strong>Deleted Accounts:</strong> Data removed within 30 days of deletion request</li>
              <li><strong>Backups:</strong> May persist in encrypted backups for up to 90 days</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
            <p className="text-gray-700 mb-4">
              Phygital is not intended for users under 13 years old. We do not knowingly collect data from children. 
              If you believe a child has provided us with personal information, please contact us.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. International Data Transfers</h2>
            <p className="text-gray-700 mb-4">
              Your data may be processed in countries outside your residence. We ensure appropriate safeguards 
              are in place for international transfers, including standard contractual clauses where applicable.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Changes to This Policy</h2>
            <p className="text-gray-700 mb-4">
              We may update this Privacy Policy periodically. We will notify you of material changes via email 
              or in-app notification. Continued use after changes constitutes acceptance.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              For privacy questions or concerns:
            </p>
            <p className="text-gray-700">
              <strong>Email:</strong> <a href="mailto:hello@phygital.systems" className="text-purple-600 hover:underline">hello@phygital.systems</a><br />
              <strong>Website:</strong> phygital.systems
            </p>
          </section>

          <section className="mb-8 p-6 bg-gray-100 rounded-xl">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">California Residents (CCPA)</h2>
            <p className="text-gray-700 mb-4">
              California residents have additional rights under the CCPA, including the right to know what personal 
              information is collected and the right to opt-out of the sale of personal information. 
              <strong> We do not sell your personal information.</strong>
            </p>
          </section>

          <section className="mb-8 p-6 bg-gray-100 rounded-xl">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">EU/UK Residents (GDPR)</h2>
            <p className="text-gray-700">
              EU and UK residents have rights under GDPR including access, rectification, erasure, restriction, 
              portability, and objection. Our legal basis for processing is consent (for preferences) and legitimate 
              interest (for service operation). Contact us at <a href="mailto:hello@phygital.systems" className="text-purple-600 hover:underline">hello@phygital.systems</a> for data protection inquiries.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPage;
